import React, { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { resolveImageUrl } from "@/lib/siteContent";

/**
 * Image carousel for package / theme cards.
 *
 * Behavior:
 *   - If images array has >1 items → shows current image + prev/next arrows on hover + dot indicators
 *   - If only 1 image → returns a single img with the existing zoom-on-hover effect (no arrows / no dots)
 *
 * Props:
 *   images: string[]   // ordered list of image URLs (uploaded paths or external)
 *   alt:    string
 *   aspect: tailwind class like 'aspect-[4/3]' (default '')
 */
export default function ImageCarousel({ images = [], alt = "", aspect = "" }) {
  const list = (images || []).filter((u) => u && String(u).trim());
  const [idx, setIdx] = useState(0);

  if (list.length === 0) return null;

  // Single image — zoom-on-hover only, no controls
  if (list.length === 1) {
    return (
      <div className={`${aspect} overflow-hidden group`}>
        <img
          src={resolveImageUrl(list[0])}
          alt={alt}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
        />
      </div>
    );
  }

  const prev = (e) => { e.preventDefault(); e.stopPropagation(); setIdx((i) => (i - 1 + list.length) % list.length); };
  const next = (e) => { e.preventDefault(); e.stopPropagation(); setIdx((i) => (i + 1) % list.length); };

  return (
    <div className={`${aspect} relative overflow-hidden group`}>
      {list.map((src, i) => (
        <img
          key={i}
          src={resolveImageUrl(src)}
          alt={`${alt} ${i + 1}`}
          loading="lazy"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${i === idx ? "opacity-100" : "opacity-0"}`}
        />
      ))}

      {/* arrows — visible on hover */}
      <button
        type="button"
        onClick={prev}
        aria-label="Previous image"
        className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/85 hover:bg-white text-cozy-brown shadow-cozy flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
      >
        <ChevronLeft className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={next}
        aria-label="Next image"
        className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/85 hover:bg-white text-cozy-brown shadow-cozy flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
      >
        <ChevronRight className="w-4 h-4" />
      </button>

      {/* dot indicators */}
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
        {list.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIdx(i); }}
            aria-label={`Go to image ${i + 1}`}
            className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-white" : "w-1.5 bg-white/60"}`}
          />
        ))}
      </div>
    </div>
  );
}

/** Pick the right image source for a card item that may have a legacy `image` (string) or new `images` (array). */
export function pickImages(item) {
  if (Array.isArray(item.images) && item.images.length > 0) return item.images;
  if (item.image) return [item.image];
  return [];
}
